import { 
  Box, 
  Card, 
  CardContent, 
  Chip,
  Divider,
  Grid, 
  Typography, 
  useTheme,
  Avatar,
  Stack,
  LinearProgress
} from "@mui/material";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { getClinicianProfile, getSessionData } from "../../../api/services/api";
import AnalyticsCard from "../../../components/AnalyticsCard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VideocamIcon from "@mui/icons-material/Videocam";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import moment from "moment";

export const Session = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { data } = useQuery(["clinician-profile"], getClinicianProfile);

  const { data: dashboardData } = useQuery(
    ["session-data", data?.data?.id],
    getSessionData,
    {
      enabled: !!data?.data?.id,
    }
  );

  const totalSessions = (dashboardData?.data?.sessionDetails?.upcoming || 0) + 
                       (dashboardData?.data?.sessionDetails?.completed || 0) + 
                       (dashboardData?.data?.sessionDetails?.rejected || 0);

  const completionRate = totalSessions > 0 
    ? Math.round(((dashboardData?.data?.sessionDetails?.completed || 0) / totalSessions) * 100)
    : 0;

  const stats = [
    {
      title: "Upcoming Sessions",
      count: dashboardData?.data?.sessionDetails?.upcoming || 0,
      route: "/sessions/upcoming-sessions",
      color: theme.colors.warning,
      icon: <CalendarTodayIcon sx={{ fontSize: 40 }} />,
      bgGradient: `linear-gradient(135deg, ${theme.colors.warning.lighter} 0%, ${theme.colors.warning.light} 100%)`,
      description: "Scheduled appointments"
    },
    {
      title: "Completed Sessions",
      count: dashboardData?.data?.sessionDetails?.completed || 0,
      route: "/sessions/completed-sessions",
      color: theme.colors.success,
      icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
      bgGradient: `linear-gradient(135deg, ${theme.colors.success.lighter} 0%, ${theme.colors.success.light} 100%)`,
      description: "Successfully finished"
    },
    {
      title: "Cancelled Sessions",
      count: dashboardData?.data?.sessionDetails?.rejected || 0,
      route: "/sessions/cancelled-sessions",
      color: theme.colors.error,
      icon: <CancelIcon sx={{ fontSize: 40 }} />,
      bgGradient: `linear-gradient(135deg, ${theme.colors.error.lighter} 0%, ${theme.colors.error.light} 100%)`,
      description: "Cancelled or rejected"
    },
  ];

  return (
    <Box mt={3}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h2" gutterBottom>
            Sessions Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track all your consultation sessions
          </Typography>
        </Box>
        <Chip 
          label={`Total: ${totalSessions} Sessions`}
          sx={{ 
            fontSize: 16, 
            fontWeight: 600, 
            px: 2, 
            py: 3,
            backgroundColor: theme.colors.primary.main,
            color: "white"
          }} 
        />
      </Box>

      {/* Quick Stats Summary */}
      <Card sx={{ mb: 3, background: `linear-gradient(135deg, ${theme.colors.primary.lighter} 0%, ${theme.colors.info.lighter} 100%)` }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: theme.colors.primary.main, width: 56, height: 56 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {completionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completion Rate
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: theme.colors.success.main, width: 56, height: 56 }}>
                  <CalendarTodayIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {moment().format("DD MMM")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Today's Date
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: theme.colors.warning.main, width: 56, height: 56 }}>
                  <PendingIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {dashboardData?.data?.sessionDetails?.upcoming || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Sessions
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Session Type Cards */}
      <Grid container spacing={3} mb={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              sx={{
                height: "100%",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: 6,
                },
              }}
              onClick={() => navigate(stat.route)}
            >
              <Box
                sx={{
                  background: stat.bgGradient,
                  p: 3,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Avatar
                    sx={{
                      bgcolor: "white",
                      color: stat.color.main,
                      width: 64,
                      height: 64,
                      boxShadow: 2,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Chip
                    label="View All"
                    size="small"
                    sx={{
                      backgroundColor: "white",
                      fontWeight: 600,
                      color: stat.color.main,
                    }}
                  />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: stat.color.main, mb: 0.5 }}>
                  {stat.count}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {stat.title}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.alpha.black[70] }}>
                  {stat.description}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Additional Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Session Progress
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Completed</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {dashboardData?.data?.sessionDetails?.completed || 0} / {totalSessions}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalSessions > 0 ? (dashboardData?.data?.sessionDetails?.completed || 0) / totalSessions * 100 : 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.colors.success.lighter,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: theme.colors.success.main,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Upcoming</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {dashboardData?.data?.sessionDetails?.upcoming || 0} / {totalSessions}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalSessions > 0 ? (dashboardData?.data?.sessionDetails?.upcoming || 0) / totalSessions * 100 : 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.colors.warning.lighter,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: theme.colors.warning.main,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Cancelled</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {dashboardData?.data?.sessionDetails?.rejected || 0} / {totalSessions}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalSessions > 0 ? (dashboardData?.data?.sessionDetails?.rejected || 0) / totalSessions * 100 : 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.colors.error.lighter,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: theme.colors.error.main,
                      },
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Consultation Modes
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: theme.colors.primary.lighter, width: 40, height: 40 }}>
                      <VideocamIcon sx={{ color: theme.colors.primary.main }} />
                    </Avatar>
                    <Typography variant="body2">Video Consultation</Typography>
                  </Box>
                  <Chip 
                    label="Available" 
                    size="small" 
                    sx={{ 
                      backgroundColor: theme.colors.success.lighter,
                      color: theme.colors.success.main,
                      fontWeight: 600
                    }} 
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: theme.colors.info.lighter, width: 40, height: 40 }}>
                      <PhoneIcon sx={{ color: theme.colors.info.main }} />
                    </Avatar>
                    <Typography variant="body2">Audio Consultation</Typography>
                  </Box>
                  <Chip 
                    label="Available" 
                    size="small" 
                    sx={{ 
                      backgroundColor: theme.colors.success.lighter,
                      color: theme.colors.success.main,
                      fontWeight: 600
                    }} 
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: theme.colors.warning.lighter, width: 40, height: 40 }}>
                      <HomeIcon sx={{ color: theme.colors.warning.main }} />
                    </Avatar>
                    <Typography variant="body2">Home Visit</Typography>
                  </Box>
                  <Chip 
                    label="On Request" 
                    size="small" 
                    sx={{ 
                      backgroundColor: theme.colors.alpha.black[10],
                      color: theme.colors.alpha.black[70],
                      fontWeight: 600
                    }} 
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
